import { useState, useCallback } from 'react';
import { api } from '../api';
import { usePolling } from '../hooks/usePolling';
import { useApp } from '../contexts/AppContext';

/**
 * MetricsBar — document status summary that auto-polls while processing is active.
 *
 * Calls GET /api/v1/documents/case/{caseId}/statistics
 * Shows: total | completed | processing | pending | failed
 * Polls every 5s while any docs are processing/pending, stops when all idle.
 */
export default function MetricsBar({ caseId }) {
  const { pricing, caseDashboard } = useApp();
  // Seed from the case-page mount dashboard envelope so we don't fire a
  // duplicate /statistics fetch on first render. Pool Governance P2.
  const [stats, setStats] = useState(() => caseDashboard?.statistics || null);
  const [error, setError] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.get(`/documents/case/${caseId}/statistics`);
      setStats(data);
      setError(false);
    } catch {
      setError(true);
    }
  }, [caseId]);

  const breakdown = stats?.status_breakdown || {};
  const hasActive = (breakdown.processing || 0) + (breakdown.pending || 0) > 0;

  // Poll every 5s while processing, every 30s when idle. When seeded
  // from the dashboard envelope, skip the immediate fetch — the poll
  // interval picks up freshness without a duplicate on-mount call.
  // Pool Governance P2.
  const seeded = caseDashboard?.statistics != null;
  usePolling(fetchStats, hasActive ? 5000 : 30000, true, !seeded);

  if (error || !stats) return null;

  const total = stats.total_documents || 0;
  const completed = breakdown.completed || 0;
  const processing = breakdown.processing || 0;
  const pending = breakdown.pending || 0;
  const failed = breakdown.failed || 0;

  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Case spend pulled from cost_events (the immutable ledger) — same
  // source as the case header's "Case spend" pill. The ledger is raw cost;
  // the header shows billed dollars (raw × the org's display margin), so the
  // same margin is applied here — otherwise one case showed two totals.
  // Includes embeddings + analysis on top of OCR/extraction.
  const showCost = pricing?.show_costs !== false;
  const margin = pricing?.margin_multiplier || 1;
  const caseTotalUsd = (stats.cost_total_usd || 0) * margin;
  const byService = stats.cost_by_service || [];
  const breakdownTitle = byService.length
    ? byService.map(s => `${s.service}: $${((s.total_usd || 0) * margin).toFixed(2)} (${s.events} events)`).join('\n')
    : 'No metered cost yet';

  return (
    <div className="metrics-bar">
      <div className="metrics-bar-items">
        <MetricPill label="Total" value={total} cls="metric-total" />
        <MetricPill label="Completed" value={completed} cls="metric-completed" />
        {processing > 0 && <MetricPill label="Processing" value={processing} cls="metric-processing" />}
        {pending > 0 && <MetricPill label="Pending" value={pending} cls="metric-pending" />}
        {failed > 0 && <MetricPill label="Failed" value={failed} cls="metric-failed" />}
        {showCost && caseTotalUsd > 0 && (
          <MetricPill
            label="Case spend"
            value={`$${caseTotalUsd.toFixed(2)}`}
            cls="metric-cost"
            title={breakdownTitle}
          />
        )}
      </div>
      {total > 0 && (
        <div className="metrics-bar-progress">
          <div className="metrics-bar-track">
            <div className="metrics-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="metrics-bar-pct" title={`${pct}% of documents fully processed`}>{pct}%</span>
        </div>
      )}
    </div>
  );
}

function MetricPill({ label, value, cls, title }) {
  return (
    <div className={`metric-pill ${cls}`} title={title}>
      <span className="metric-value">{value}</span>
      <span className="metric-label">{label}</span>
    </div>
  );
}
